import { palette } from './palette'

/**
 * 의미 토큰(semantic tokens). 컴포넌트가 실제로 참조하는 유일한 색 계층이다.
 *
 * 토스 문법(reference/design, 2026-07-31)에 맞춰 **라이트가 기준 테마**다 —
 * 흰 바탕 위에 여백과 타이포그래피로 위계를 만드는 구성. 브랜드 액센트(코발트·연두)는 유지.
 * 다크는 스크린샷이 없어 토스 다크의 문법(진회색 카드, 밝은 액센트)을 근사했다.
 */
export interface ThemeColors {
  /** 화면 최하단 배경 */
  background: string
  /** 카드·시트 등 배경 위 한 단 */
  surface: string
  /** surface 위 한 단 더 — 카드 속 인셋 필드(토스의 원화/달러 박스), 회색 보조 버튼 */
  surfaceRaised: string
  /** 눌린/호버 상태의 표면 */
  surfaceHover: string

  border: string
  borderStrong: string

  /** 본문 */
  text: string
  /** 보조 설명 */
  textMuted: string
  /** 라벨·캡션 */
  textSubtle: string
  /** 액센트 배경 위에 올라가는 글자 */
  textOnAccent: string

  accent: string
  accentHover: string
  accentSurface: string

  success: string
  successSurface: string
  warning: string
  warningSurface: string
  danger: string
  dangerSurface: string
  /**
   * `danger` 를 **면으로 깔았을 때**(파괴적 버튼·스와이프 삭제 타일) 그 위의 글자·아이콘.
   *
   * **`textOnAccent` 를 재사용하면 안 된다.** 그건 서비스 액센트에 맞춰진 값이라 액센트마다
   * 흑/백이 뒤집힌다 — 붉은 면 위 글자색이 "어느 서비스냐"로 우연히 정해진다. 2026-08-12 에
   * 실제로 그 상태였다: 라이트는 전 서비스가 흰 글자 on `danger[500]` = **3.71:1**,
   * 다크는 코발트·바이올렛(흰 `textOnAccent`)이 **2.99:1** 로 AA(4.5)는 물론 3:1도 못 넘었다.
   *
   * 값은 액센트가 아니라 **`danger` 면에서 직접** 고른다 — 라이트 흰 글자 on `danger[600]` = 5.26,
   * 다크 `neutral[950]` on `danger[400]` = 6.34. 라벨은 14px semibold(큰 버튼도 18px)라
   * WCAG 큰 글자 예외(18.66px bold)에 걸리지 않으므로 기준은 항상 4.5다.
   *
   * `success`·`warning` 에는 짝 토큰을 만들지 않았다 — 지금 면으로 깔리는 곳이 없다(상태 점·글자뿐).
   * 두 번째 사용처가 생길 때 승격한다 (docs/rules.md §공용 코드 경계).
   */
  textOnDanger: string

  /**
   * 소셜 로그인 버튼 — **각 사가 정한 색이라 우리 팔레트로 바꾸면 안 된다.**
   * 브랜드 가이드라인 위반이고, 어느 서비스로 로그인하는지 알아보는 단서이기도 하다.
   * 라이트/다크 값이 다른 것은 각 사가 다크용 색을 따로 정해 둔 경우다.
   */
  socialKakao: string
  textOnSocialKakao: string
  /** 구글은 배경이 흰색/검정이라 테두리로 형태를 잡는다 — 테두리 색까지 규격이다 */
  socialGoogle: string
  textOnSocialGoogle: string
  socialGoogleBorder: string
  socialApple: string
  textOnSocialApple: string

  /**
   * 로딩 자리(스켈레톤) — **이 두 색만 쓴다** (2026-08-07 사용자 지시: 회색·어두운 회색 두 색으로
   * 단조롭게, 전 서비스 공통). `skeleton`이 바탕, `skeletonStrong`이 펄스가 도달하는 어두운 쪽이다.
   *
   * 표면 토큰(`surfaceRaised`·`surfaceHover`)을 쓰지 않는다 — 그쪽은 칩·입력칸이 함께 쓰는
   * 값이라 로딩 블록의 대비를 거기에 묶으면 한쪽을 고칠 때 다른 쪽이 따라 움직인다.
   *
   * **고르는 규칙 둘.** ① 채도를 5% 안쪽으로 묶는다 — 그래야 회색으로 읽힌다.
   * (예전에는 "테마의 온도에 맞춘다"가 규칙이었다. 라이트 바탕이 크림색이던 때의 이야기이고,
   * 2026-08-07 에 흰 바탕으로 바뀌면서 두 테마 모두 무채색 램프를 쓴다.)
   * ② **두 값을 램프에서 두 단 이상 벌린다.** 쉴 때는 조용해야 하니 바탕은 옅게 두더라도,
   * 펄스가 눈에 보여야 "로딩 중"이 전달된다. 한 단만 벌리면(라이트 200→300) 펄스 대비가 1.19 로
   * 사실상 정지 화면이 된다 — 2026-08-07 에 다크에서 실제로 그 상태였다(두 토큰이 같은 값).
   * 실측 기준: 라이트 200→400 = 1.63, 다크 800→700 = 1.55 로 두 테마의 호흡이 맞는다.
   *
   * 서비스 액센트(코발트·연두·앰버·바이올렛·로즈)와는 무관하다 — `AccentSet`에 없으므로 어느 앱에서나
   * 같은 회색이다. **액센트·상태색을 스켈레톤에 끌어오지 말 것.**
   */
  skeleton: string
  skeletonStrong: string

  /** 모달 뒤 딤 */
  overlay: string
}

export type ThemeName = 'dark' | 'light'

export interface Theme {
  name: ThemeName
  colors: ThemeColors
}

/** 액센트 한 벌 — 글자색까지 세트다 (밝은 액센트는 검은 글자, 어두운 액센트는 흰 글자) */
export interface AccentSet {
  accent: string
  accentHover: string
  accentSurface: string
  textOnAccent: string
}

/**
 * 서비스별 액센트. 기본은 브랜드 색(cobalt)이고, 서비스 성격이 뚜렷하면 전용 액센트를 쓴다.
 * ThemeProvider(ui-web·ui-native)의 accent prop으로 갈아끼운다 — 나머지 토큰은 공통.
 *
 * 라이트 액센트는 "흰 바탕에서 글자로 써도 4.5:1"인 스텝을 고른다 — leaf가 800인 이유.
 */
export const accents = {
  /** 브랜드 기본 — 전 서비스 공통 */
  cobalt: {
    dark: {
      accent: palette.accent[400],
      accentHover: palette.accent[300],
      accentSurface: palette.accent[950],
      textOnAccent: palette.white,
    },
    light: {
      accent: '#2f67d8',
      accentHover: '#004dbc',
      accentSurface: palette.accent[50],
      textOnAccent: palette.white,
    },
  },
  /** 식집사 — 식물·성장 */
  leaf: {
    dark: {
      accent: palette.leaf[400],
      accentHover: palette.leaf[300],
      accentSurface: palette.leaf[950],
      textOnAccent: palette.neutral[950],
    },
    light: {
      accent: '#11693d',
      accentHover: '#00522d',
      accentSurface: palette.leaf[50],
      textOnAccent: palette.white,
    },
  },
  /** 시험보소 — 마감·알림. 라이트는 amber-600(#d96f0e)이 흰 배경 대비가 3.0으로 낮아 700을 쓴다 */
  amber: {
    dark: {
      accent: palette.amber[400],
      accentHover: palette.amber[300],
      accentSurface: palette.amber[950],
      textOnAccent: palette.neutral[950],
    },
    light: {
      accent: '#b3560c',
      accentHover: '#8f4511',
      accentSurface: palette.amber[50],
      textOnAccent: palette.white,
    },
  },
  /** 첫장면 — 예식·축하. danger 와 구분되도록 채도를 낮춘 로즈 */
  rose: {
    dark: {
      accent: palette.rose[400],
      accentHover: palette.rose[300],
      accentSurface: palette.rose[950],
      textOnAccent: palette.neutral[950],
    },
    light: {
      accent: '#98354f',
      accentHover: '#7a2d41',
      accentSurface: palette.rose[50],
      textOnAccent: palette.white,
    },
  },
  /** Peekmo — 잠금화면 메모. 라이트는 teal-600(#0c9483)이 흰 배경 대비 3.8이라 700을 쓴다 (amber와 같은 사유) */
  teal: {
    dark: {
      accent: palette.teal[400],
      accentHover: palette.teal[300],
      accentSurface: palette.teal[950],
      textOnAccent: palette.neutral[950],
    },
    light: {
      accent: '#0a756b',
      accentHover: '#0c5d55',
      accentSurface: palette.teal[50],
      textOnAccent: palette.white,
    },
  },
  /** cotton — 디자인 도구. 코발트(228)·연두(105)와 hue 거리가 멀어 충돌 적음 */
  violet: {
    dark: {
      accent: palette.violet[400],
      accentHover: palette.violet[300],
      accentSurface: palette.violet[950],
      textOnAccent: palette.white,
    },
    light: {
      accent: '#5c3abf',
      accentHover: '#4e28b0',
      accentSurface: palette.violet[50],
      textOnAccent: palette.white,
    },
  },
  /**
   * 먹 — **색을 아예 안 쓰는 서비스용** (낌새, 2026-08-29 사용자 지시 "흑백스타일").
   *
   * 다른 액센트가 전부 색상환의 한 자리를 잡는 것과 달리 이건 중립축 위에 있다.
   * 그래서 **강조는 색이 아니라 명도로만** 걸린다 — 라이트에서는 거의 검정(`neutral[900]`),
   * 다크에서는 거의 흰색이다.
   *
   * `accentSurface` 는 배경과 겨우 구별되는 밝기여야 한다. 여기서 색을 조금이라도 섞으면
   * 흑백이 아니게 되고, 너무 진하면 뒤에 깔린 글씨를 잡아먹는다.
   */
  ink: {
    dark: {
      accent: palette.neutral[100],
      accentHover: palette.white,
      accentSurface: palette.neutral[800],
      // 밝은 먹 위에는 검정 글씨다 — 다른 액센트가 전부 흰 글씨인 것과 반대다
      textOnAccent: palette.neutral[950],
    },
    light: {
      accent: palette.neutral[900],
      accentHover: palette.neutral[950],
      accentSurface: palette.neutral[100],
      textOnAccent: palette.white,
    },
  },
} as const satisfies Record<string, Record<ThemeName, AccentSet>>

export type AccentName = keyof typeof accents

const light: Theme = {
  name: 'light',
  colors: {
    // **바탕은 흰색이다 (2026-08-07 사용자 지시 — "누런 배경 빼라, 그냥 흰색", 전 서비스 공통).**
    // 예전에는 크림색(`#fffbf5`)이었다. Pinterest 레퍼런스의 따뜻한 흰 바탕을 옮긴 것이었는데,
    // 화면 전체를 덮다 보니 흰 카드와 나란히 놓였을 때 누렇게 읽혔다.
    //
    // **바탕과 카드가 같은 흰색이라 카드는 이제 테두리로만 구분된다.** `Card` 가 이미
    // `borderWidth: 1` 을 그려서 그대로 보이지만, 새 표면을 만들 때 테두리를 빼면 화면에서
    // 사라진다는 뜻이다 — 배경색 차이에 기대지 말 것.
    background: palette.white,
    surface: palette.white,
    // 흰 바탕 위에서 회색으로 읽혀야 하는 것들(칩·보조 버튼·입력칸·뱃지)이 쓴다.
    // neutral[50](#f9fafb)은 흰 바탕에서 거의 안 보여 한 단 내린 100 을 바탕값으로 잡았다
    surfaceRaised: palette.neutral[100],
    surfaceHover: palette.neutral[200],

    // 카드·행 경계. 바탕이 흰색이 된 뒤로는 **이것이 유일한 구분선**이다
    border: palette.neutral[200],
    borderStrong: '#cac4d6',

    text: '#1c274c',
    textMuted: '#5c688d',
    textSubtle: '#7a829d',
    ...accents.cobalt.light,

    success: palette.success[600],
    successSurface: '#e7f8f0',
    warning: palette.warning[600],
    warningSurface: '#fff3d9',
    // 예전에는 토스 레드(`danger[500]` #f04452) 그대로였다 — 등락·경고 숫자가 크고 굵게
    // 쓰인다는 이유였는데, **흰 바탕에서 3.71:1** 이라 글자로 써도(작은 오류 문구) 흰 글자를
    // 얹는 면으로 써도(삭제 버튼) AA 에 못 미쳤다. 액센트가 이미 지키는 규칙 — "라이트는
    // 흰 바탕에서 글자로 써도 4.5:1 인 스텝을 고른다"(위 `accents` 주석, leaf 가 800 인 이유) —
    // 을 상태색에도 그대로 적용해 한 단 내렸다 (2026-08-12). 양쪽 다 5.26:1 이 된다
    danger: palette.danger[600],
    dangerSurface: '#fdebed',
    textOnDanger: palette.white,

    // 카카오 — KakaoTalk Yellow. 다크에서도 같은 노랑을 쓰라고 가이드가 정한다
    socialKakao: '#fee500',
    textOnSocialKakao: '#191600',
    // 구글 — 라이트: 흰 배경 + #747775 테두리 + #1f1f1f 라벨
    socialGoogle: '#ffffff',
    textOnSocialGoogle: '#1f1f1f',
    socialGoogleBorder: '#747775',
    // 애플 — 라이트: 검정 배경 + 흰 라벨
    socialApple: '#000000',
    textOnSocialApple: '#ffffff',

    // 무채색 두 단. 예전에는 따뜻한 인라인 값(`#e5e1da`·`#c8c4be`)이었다 — 크림색 바탕 위에서
    // 차가운 회색이 찬 패치처럼 떠 보였기 때문이다. 바탕이 흰색이 되면서 그 이유가 사라져
    // 팔레트 램프로 되돌렸다 (2026-08-07)
    //
    // 바탕은 200 으로 옅게(흰 카드 대비 1.23 — 쉴 때 조용하다), 펄스는 **300 이 아니라 400** 이다.
    // 300 이면 펄스 대비가 1.19 로 움직임이 안 보인다. 400 이면 1.63 으로 다크(1.55)와 같은 호흡이다
    skeleton: palette.neutral[200],
    skeletonStrong: palette.neutral[400],

    overlay: 'rgba(25, 31, 40, 0.5)',
  },
}

const dark: Theme = {
  name: 'dark',
  colors: {
    background: palette.neutral[950],
    surface: palette.neutral[900],
    surfaceRaised: palette.neutral[800],
    surfaceHover: palette.neutral[800],

    border: palette.neutral[800],
    borderStrong: palette.neutral[700],

    text: palette.neutral[50],
    textMuted: palette.neutral[400],
    textSubtle: palette.neutral[500],
    ...accents.cobalt.dark,

    success: palette.success[400],
    successSurface: palette.success[900],
    warning: palette.warning[400],
    warningSurface: palette.warning[900],
    // 다크는 `danger` 가 **글자로 먼저** 쓰인다 — 어두운 바탕 위 오류 문구라 밝은 쪽(400)이어야
    // 한다(#101013 배경에서 6.34:1). 그래서 면으로 깔릴 때는 밝은 면이 되고, 글자는 검은 쪽이다 —
    // 다크 액센트(teal·leaf·amber·rose)가 400 위에 `neutral[950]` 을 얹는 것과 같은 규칙이다.
    // 흰 글자를 얹으면 2.99:1 로 떨어진다
    danger: palette.danger[400],
    dangerSurface: palette.danger[900],
    textOnDanger: palette.neutral[950],

    socialKakao: '#fee500',
    textOnSocialKakao: '#191600',
    // 구글 다크 — #131314 배경 + #8e918f 테두리 + #e3e3e3 라벨 (Google Identity 가이드)
    socialGoogle: '#131314',
    textOnSocialGoogle: '#e3e3e3',
    socialGoogleBorder: '#8e918f',
    // 애플 다크 — 흰 배경 + 검정 라벨
    socialApple: '#ffffff',
    textOnSocialApple: '#000000',

    // 다크는 표면이 이미 차가운 슬레이트라 무채색 축을 그대로 쓴다 — surfaceRaised·borderStrong 과
    // 같은 두 단이어서 새 색이 없다(온도 −24/−26 = 주변과 동일).
    // 예전에는 surfaceRaised·surfaceHover 가 **둘 다 neutral[800]** 이라 다크에서 펄스가
    // 같은 색 사이를 오갔다(= 애니메이션이 아예 안 보였다). 두 단을 벌려 그것도 고쳤다
    skeleton: palette.neutral[800],
    skeletonStrong: palette.neutral[700],

    overlay: 'rgba(0, 0, 0, 0.66)',
  },
}

export const themes: Record<ThemeName, Theme> = { dark, light }

/** 토스 문법의 기준 테마 — 라이트 (2026-07-31, 이전에는 다크 기본) */
export const defaultTheme = light
